// jobCardProps — how a job from GET /api/field/today becomes a JobCard, with
// no React in it so it can be tested without a browser.
//
// ── Why this is a module and not four lines in Today.js ─────────────────────
//
// It was four lines in Today.js, and they were:
//
//     tone={job.blocked ? 'stop' : 'sched'}
//     meta={[place, job.meta].filter(Boolean).join(' · ')}
//
// which is the whole of what the crew screen said about a job. Two things
// follow from it and the owner reported both:
//
//   - **No status.** `tone` was only ever 'stop' or 'sched', so an active job,
//     a paused one and one nobody has started looked identical. "Can't
//     distinguish active/todo/archived jobs."
//   - **One grey string.** The place, the first line of the description and
//     the estimate were joined into a single line of `--fw-muted` 13px that
//     wrapped to five lines and said nothing at a glance.
//
// The vocabulary is NOT invented here. `apps/shared/jobFields.js` already owns
// the five statuses, their labels and their badge tones for Office and
// Dispatch, and CLAUDE.md is explicit that a second copy of a rule in a screen
// is how a screen ends up disagreeing with the server.
//
// ── What used to be Field-only, and is not any more ─────────────────────────
//
// `server/routes/field.js shapeJob()` returns BOTH `blocked` (a boolean) and
// `blocked_reason`. `jobBadges()` keys off `blocked_reason`, which the Field
// bundle does send — but the offline cache and every test fixture in
// `apps/harness.test.js` carry only the boolean. A card built from a cached
// bundle would therefore have dropped the Blocked badge, which is exactly the
// job a crew member must not drive to.
//
// `jobCardBadges` and `jobTone` handled that, HERE, and on 2026-09-10 the
// Dispatch board turned out to have neither — no status badge at all, and its
// own local tone map that answered `neutral` for to do, active and paused.
// Both moved to apps/shared/jobFields.js and this module re-exports them, so
// the two screens that draw the same five statuses on the same primitive read
// them from one place.

import { crewSummary, jobCardTone, jobCardBadges } from '../shared/jobFields';
import { plural } from './fmt';

/**
 * The card's left border. This is the COLOUR half of the status signal; the
 * badge is the text half, and neither is ever shown without the other.
 *
 * These are JOBCARD_TONES (ui/fw/components/JobCard.js). That used to say they
 * were "a different scale from the Pill tones jobBadges() returns — a border
 * has 'sched', a pill does not", and the code disagrees: JOBCARD_TONES and
 * PILL_TONES are the SAME six values in a different order. Corrected rather
 * than left, per CLAUDE.md — a note that has quietly become false is worse than
 * no note, because it gets followed, and this one would have argued against
 * the single map that fixes the Office greys.
 *
 * Blocked wins over everything: it is the one state that means "do not go",
 * and it is why `stop` is not spent on any lifecycle stage.
 */
// The shared map (apps/shared/jobFields.js). This file used to keep its own
// copy, which happened to be the CORRECT one — Office's was the broken one —
// but two copies that agree today is how five copies that disagree start.
//
// The FUNCTION moved there too, 2026-09-10, when Dispatch turned out to be
// carrying a fifth copy that answered `neutral` for to do, active AND paused.
// Re-exported under its own name so this module's ~4 callers and
// jobCardProps.test.js are untouched.
// A `const` alias and not `export { … } from`: a re-export creates no LOCAL
// binding, so jobCardProps() below would throw ReferenceError on a name this
// file appears to export.
export const jobTone = jobCardTone;

/**
 * Where the work is, and WHOSE it is — both, not whichever one exists.
 *
 * This was `property_name || client_name`, so on every job that has a
 * property — which is most of them — the client never appeared on the card at
 * all. The owner, 2026-09-10: "Need to see who's assigned/unassigned, client,
 * property". They are different questions to somebody pulling up: the property
 * is the gate you stop at, the client is whose gate it is, and a crew member
 * who cannot name the customer standing in the drive is the reason this line
 * exists.
 *
 * The property leads because it is the one you navigate by. Either half alone
 * is the whole line when the other is absent, and a job with neither renders
 * no meta line rather than a stray separator — the No client badge is what
 * says so on that card.
 *
 * Deliberately NOT the address: it is on the job screen one tap away, in its
 * own card with a directions link (Job.js), and a full street address in a
 * 13px muted line wraps a phone card to three lines for something nobody
 * copies off a list.
 */
export function jobPlace(job) {
  if (!job) return null;
  return [job.property_name, job.client_name].filter(Boolean).join(' \u00b7 ') || null;
}

/**
 * The short numbers, in the order they matter to somebody about to start:
 * how much of the work list is done, then how long it is meant to take.
 *
 * The server's own `meta` string is deliberately NOT used. It is
 * "<first line of the description> · <n> hrs est." — a sentence and a number
 * welded together, and the sentence is the part that wrapped the card to
 * 344px tall. The description is on the job screen, one tap away.
 */
export function jobFacts(job) {
  if (!job) return [];
  const out = [];
  const crew = crewFact(job);
  if (crew) out.push(crew);
  if (job.subtask_total > 0) out.push(`${job.subtask_done || 0} of ${job.subtask_total} done`);
  if (job.estimated_hours) out.push(`${plural(job.estimated_hours, 'hr')} est.`);
  return out;
}

/**
 * "Dave +2 crew" — or nothing at all.
 *
 * The classic list row carried this exact string
 * (git show d25258b^:client/src/components/Jobs/Jobs.js, L1136) and the
 * rebuilt card carried nothing, so a crew member could not tell from their day
 * whether anybody else was going (docs/PARITY_2026-09-08.md §8). It is FIRST
 * in the fact list because it is the one that changes what somebody does
 * before they set off; a work-list count does not.
 *
 * Only when there is more than one person on the job. Every card on a crew
 * member's own day names them as the lead, so printing a name on all of them
 * is noise that pushes the facts that differ off the row — and the signal
 * being restored here is precisely "you are NOT on your own".
 */
export function crewFact(job) {
  const crew = (job && job.assignees) || [];
  if (crew.length < 2) return null;
  return `${crewSummary(job)} crew`;
}

/**
 * Status, and anything else a person must know before they walk up to it.
 *
 * The body moved to apps/shared/jobFields.js on 2026-09-10 — Dispatch's board
 * shows the same five statuses on the same primitive and had no badges at all.
 * Re-exported, so Today.js and this file's tests do not move.
 */
export { jobCardBadges };

/**
 * ── The badge slot carries the EXCEPTION, not the rule (2026-09-12) ─────────
 *
 * Measured in Chromium at 390x844 against the built app, signed in as a crew
 * member with twelve jobs seeded for the company's today:
 *
 *     status pills on screen       13
 *     of which "Active"             7
 *     of which "To do"              4
 *     cards fully above the fold    3 of 12
 *
 * Every card carried a pill, and eleven of the thirteen said the job was open
 * work — which is what every job on a day list is, by construction:
 * `workerJobs()` in server/routes/field.js selects
 * `status IN ('active','todo','paused','in_progress')`.
 *
 * A badge that is true of every row is not a signal. It costs more than the
 * space it takes, because it teaches a crew member that badges are decoration
 * — so the one that means *do not drive there* arrives on a screen where
 * nobody reads badges any more. That is the failure this list exists to stop.
 *
 * `todo` and `active` are one thing to the person holding the phone: work that
 * is not done. Which of the two a row is in is an OFFICE distinction (has
 * anybody marked it started), and it changes nothing about what the crew
 * member does next. Paused, Completed and anything unrecognised do change it,
 * so they keep their word.
 */
export const ROUTINE_STATUSES = ['todo', 'active'];

/**
 * The badges this card actually shows: `jobCardBadges` minus a status that is
 * simply what the rest of the day is.
 *
 * NOT a change to `jobCardBadges`. That lives in apps/shared/jobFields.js and
 * Office and Dispatch draw record lists off it, where a status genuinely
 * varies row to row — an Archived job beside an Active one — and where the
 * pill is the only thing that says which. This is the FIELD card's reading of
 * that answer, and it is the only place the rule is written down.
 *
 * Blocked and No client always survive. One means do not go; the other is the
 * crew member's cue to tell the office a job cannot be billed.
 */
export function cardBadges(job) {
  if (!job) return [];
  const status = String(job.status || '');
  if (!ROUTINE_STATUSES.includes(status)) return jobCardBadges(job);
  return jobCardBadges(job).filter((b) => b.key !== 'status');
}

/**
 * The card's left border — which may only ever say what a badge on the card
 * already says in words.
 *
 * This is the half that would have been easy to forget, and forgetting it
 * inverts the defect rather than fixing it: drop the word "Active" and keep
 * the orange stripe, and the card asserts a state written nowhere on it, which
 * is precisely the colour-alone signalling docs/APP_REBUILD.md §3.3 forbids
 * and that the badge slot was built to replace.
 *
 * So: no surviving badge, no colour. Blocked outranks everything, for the
 * reason jobCardTone already gives — it is the one state that is not a stage.
 * Otherwise the first surviving badge lends its tone, so the border and the
 * leftmost pill are the same fact twice rather than two claims.
 */
export function cardTone(job) {
  const badges = cardBadges(job);
  if (!badges.length) return 'neutral';
  if (job && (job.blocked || job.blocked_reason)) return 'stop';
  return badges[0].tone || 'neutral';
}

/**
 * The card's date column, on a screen that already names a day.
 *
 * `timeLabel()` in server/routes/field.js returns the scheduled date's weekday
 * for ANY scheduled_date. Its own comment says "the date's weekday when it is
 * not today, otherwise nothing"; the code does not do that. Measured off the
 * running server on 2026-09-12, all twelve of a crew member's jobs came back
 * `time_label: "Sat 12"` — and JobCard.css puts that column FIRST, in the
 * display font at --fw-text-lg. Twelve identical dates in the loudest type on
 * the screen, on the screen called Today.
 *
 * Corrected here rather than in the server, because the label is not wrong.
 * It is exactly right on an overdue job — the server puts those on a crew
 * member's list and the date is the only thing that says why — and on anything
 * scheduled for another day. What is wrong is spending the slot on it when the
 * card is already sitting on the day it names.
 *
 * `today` is the COMPANY's today off the bundle (utils/dateRange.js). A phone
 * must never work that out for itself: this container runs UTC and the company
 * is in Connecticut. Passed nothing, the label is kept — losing a date is
 * worse than repeating one, and an old cached bundle is a real caller.
 */
export function dateLabel(job, today) {
  const label = (job && job.time_label) || '';
  if (!label || !today) return label;
  return job.scheduled_date === today ? '' : label;
}

/**
 * Everything a JobCard needs about a job except its onClick. Spread it:
 *
 *     <JobCard {...jobCardProps(job, { today })} onClick={() => openJob(job.id)} />
 *
 * `today` is optional and comes from the /field/today bundle. Without it the
 * card renders exactly as it did before this option existed.
 */
export function jobCardProps(job, { today = null } = {}) {
  return {
    time: dateLabel(job, today),
    title: job && job.title,
    meta: jobPlace(job),
    facts: jobFacts(job),
    badges: cardBadges(job),
    tone: cardTone(job),
  };
}
